import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import QRCode from 'qrcode';
import { auth, functions } from '../../firebase-client';
import { useOnlineStatus } from '../../utils/onlineStatus';
import { cacheRideDeclarationOfflineProof, readRideDeclarationOfflineProof } from '../../utils/rideDeclarationOffline';
import './rideDeclarations.css';

const createProof = httpsCallable(functions, 'createRideDeclarationProof');
const createOfflineProof = httpsCallable(functions, 'createRideDeclarationOfflineCredential');

function millis(value) {
  const numericValue = Number(value);
  if (Number.isFinite(numericValue)) return numericValue;
  if (typeof value?.toMillis === 'function') return value.toMillis();
  if (typeof value?.toDate === 'function') return value.toDate().getTime();
  const seconds = Number(value?.seconds ?? value?._seconds);
  const nanoseconds = Number(value?.nanoseconds ?? value?._nanoseconds ?? 0);
  if (Number.isFinite(seconds) && Number.isFinite(nanoseconds)) return (seconds * 1000) + Math.floor(nanoseconds / 1000000);
  const parsed = new Date(value || 0).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
}

function dateTime(value) {
  const valueMillis = millis(value); return valueMillis ? new Date(valueMillis).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }) : '';
}

export default function RideDeclarationProof() {
  const { teamId } = useParams(); const online = useOnlineStatus();
  const [proof, setProof] = useState(null); const [offlineProof, setOfflineProof] = useState(null); const [qr, setQr] = useState('');
  const [error, setError] = useState(''); const [loading, setLoading] = useState(false); const [remaining, setRemaining] = useState(0);
  const userId = auth.currentUser?.uid || '';

  const loadCachedProof = useCallback(async () => {
    if (!userId) return null;
    const cached = await readRideDeclarationOfflineProof({ teamId, userId }).catch(() => null);
    setOfflineProof(cached); return cached;
  }, [teamId, userId]);

  const refresh = useCallback(async () => {
    if (!online) { await loadCachedProof(); return; }
    setLoading(true); setError('');
    try {
      const result = await createProof({ teamId }); setProof(result.data);
      try {
        const offlineResult = await createOfflineProof({ teamId });
        setOfflineProof(offlineResult.data);
        if (userId) await cacheRideDeclarationOfflineProof({ teamId, userId, proof: offlineResult.data });
      } catch (offlineError) {
        setError(`Live proof is ready, but offline backup could not be refreshed: ${offlineError?.message || 'unknown error'}`);
      }
    } catch (err) { setError(err?.message || 'Could not generate live proof.'); }
    finally { setLoading(false); }
  }, [loadCachedProof, online, teamId, userId]);

  useEffect(() => { loadCachedProof().finally(() => { if (online) refresh(); }); }, [loadCachedProof, online, refresh]);
  useEffect(() => {
    const tick = () => {
      const next = Math.max(0, millis(proof?.expiresAt) - Date.now()); setRemaining(next);
      if (proof && next > 0 && next < 45000 && online && !loading) refresh();
    };
    tick(); const timer = window.setInterval(tick, 1000); return () => window.clearInterval(timer);
  }, [loading, online, proof, refresh]);

  const presentation = online ? proof : offlineProof;
  const valid = online ? Boolean(proof && remaining > 0) : Boolean(offlineProof && millis(offlineProof.expiresAt) > Date.now());
  const qrValue = useMemo(() => {
    if (!presentation || !valid) return '';
    if (!online) return presentation.credential || '';
    return `${window.location.origin}/teams/${encodeURIComponent(teamId)}/ride-declaration/verify?code=${encodeURIComponent(presentation.code)}`;
  }, [online, presentation, teamId, valid]);
  useEffect(() => {
    let cancelled = false;
    if (!qrValue) { setQr(''); return undefined; }
    QRCode.toDataURL(qrValue, { width: 440, margin: 2, errorCorrectionLevel: 'M' }).then(value => { if (!cancelled) setQr(value); }).catch(() => { if (!cancelled) setQr(''); });
    return () => { cancelled = true; };
  }, [qrValue]);

  const seconds = Math.ceil(remaining / 1000);
  return <main className="ride-proof-page"><header><Link className="btn btn--secondary" to={`/teams/${teamId}`}>Back to club</Link><span>IAM RoadSmart</span><h1>Ride declaration proof</h1><p>Show this screen to an authorised checker before your ride.</p></header>
    {!online && <div className="ride-proof-alert">You are offline. A previously saved proof can still be checked and the check will sync when signal returns.</div>}{error && <p className="error-message" role="alert">{error}</p>}
    {online && offlineProof && <div className="ride-offline-ready" role="status"><strong>Going off grid? You’re ready.</strong><span>A QR code has been saved on this device and will work without signal until {dateTime(offlineProof.expiresAt)}.</span><small>The checker should also open their checking screen before losing signal.</small></div>}
    {loading && !presentation && <section className="ride-proof-card"><p>Preparing live and offline proof…</p></section>}
    {!online && !presentation && <section className="ride-proof-card is-expired"><p>No valid offline proof is saved on this device. Reconnect once to prepare offline use.</p></section>}
    {presentation && <section className={`ride-proof-card ${valid ? 'is-current' : 'is-expired'}`}>
      <div className="ride-proof-state"><span aria-hidden="true">{valid ? '✓' : '!'}</span><div><strong>{valid ? online ? 'Declaration current' : 'Offline proof ready' : 'Proof expired'}</strong><small>{valid ? online ? `Live for ${seconds} seconds` : `Valid offline until ${dateTime(presentation.expiresAt)}` : 'Reconnect to refresh'}</small></div></div>
      <p className="ride-proof-group">{presentation.teamName}</p><h2>{presentation.memberName}</h2><p className="ride-proof-number">IAM membership {presentation.membershipNumber}</p><div className="ride-proof-meta"><span>Declaration version {presentation.versionNumber}</span><span>Signed {dateTime(presentation.acceptedAt)}</span></div>
      {qr && valid && <img src={qr} alt={`QR code for this ${online ? 'live' : 'offline'} declaration proof`} className="ride-proof-qr" />}
      {online && presentation.code && <div className="ride-proof-code"><span>Manual code</span><strong>{presentation.code}</strong></div>}
      <button className="button button-secondary" type="button" onClick={refresh} disabled={loading || !online}>{loading ? 'Refreshing…' : online ? 'Refresh proof' : 'Reconnect to refresh'}</button>
      <small className="ride-proof-note">{online ? offlineProof ? `Offline backup saved until ${dateTime(offlineProof.expiresAt)}.` : 'Preparing an offline backup requires a connection.' : 'The checker will save this check on their device and sync it automatically.'}</small>
    </section>}
  </main>;
}
